import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/auth-provider';
import { usePermissions } from '@/lib/permissions';
import { LoadingScreen } from '@/components/layout/loading-screen';
import type { PermissionCode, RoleSlug } from '@/lib/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: RoleSlug;
  requiredPermission?: PermissionCode;
  /** User needs any one of these permissions */
  requiredAnyPermission?: PermissionCode[];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiredPermission,
  requiredAnyPermission,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { checkRole, checkPermission, checkAnyPermission, isLoading: permLoading } = usePermissions();
  const location = useLocation();

  if (authLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  if (requiredRole || requiredPermission || (requiredAnyPermission && requiredAnyPermission.length > 0)) {
    if (permLoading) {
      return <LoadingScreen />;
    }

    if (requiredRole && !checkRole(requiredRole)) {
      return <Navigate to="/unauthorized" replace />;
    }

    if (requiredPermission && !checkPermission(requiredPermission)) {
      return <Navigate to="/unauthorized" replace />;
    }

    if (
      requiredAnyPermission &&
      requiredAnyPermission.length > 0 &&
      !checkAnyPermission(requiredAnyPermission)
    ) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
